/**
 * The browser side of the sign-in: authorization code flow with PKCE.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO, so that nobody reads more security into it than it
 * has:
 *   - It does not verify the identity token's signature. The client is not the
 *     enforcement point; the API validates every access token it is given against the
 *     provider's published keys, and a browser that lied to itself would still be refused
 *     at the only boundary that matters.
 *   - It does not refresh. The access token lives for the realm's five minutes and the
 *     provider's session outlives it, so a silent renewal is a small addition later. It is
 *     not here because an untested renewal path is worse than a visible sign-in.
 *   - It keeps the token in sessionStorage. That is readable by any script running on this
 *     origin, so it is a trade and not an oversight: it survives a page reload, it dies
 *     with the tab, and it is confined to one origin. The answer for production is a
 *     backend-for-frontend with an httpOnly cookie, which is its own work order.
 *
 * NO CREDENTIAL IS HANDLED HERE. The password is typed into the identity provider's own
 * page, on its own origin, and this code never sees it (SECURITY_STANDARDS rule S9).
 */

import { codeChallengeS256, randomUrlSafe } from "./pkce";
import { discoverEndpoints, loadAuthConfig } from "./discovery";

/** The path the provider redirects back to. It is also a redirect URI in the realm. */
export const CALLBACK_PATH = "/callback";

const SESSION_KEY = "qualify.session";
const PENDING_KEY = "qualify.signin";

export interface Session {
  readonly accessToken: string;
  readonly idToken: string | null;
  readonly expiresAt: number;
}

interface PendingSignIn {
  readonly state: string;
  readonly verifier: string;
  readonly redirectUri: string;
}

// Anything read back out of storage is untrusted input: another script on this origin, or
// an earlier version of this code, could have written it. It is narrowed field by field
// rather than cast into shape.
function readJson(key: string): Record<string, unknown> | null {
  const raw = sessionStorage.getItem(key);
  if (raw === null) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) {
      return null;
    }
    return parsed as Record<string, unknown>;
  } catch {
    return null;
  }
}

function readSession(): Session | null {
  const value = readJson(SESSION_KEY);
  if (value === null) {
    return null;
  }
  const { accessToken, idToken, expiresAt } = value;
  if (typeof accessToken !== "string" || typeof expiresAt !== "number") {
    return null;
  }
  return { accessToken, idToken: typeof idToken === "string" ? idToken : null, expiresAt };
}

function readPendingSignIn(): PendingSignIn | null {
  const value = readJson(PENDING_KEY);
  if (value === null) {
    return null;
  }
  const { state, verifier, redirectUri: uri } = value;
  if (typeof state !== "string" || typeof verifier !== "string" || typeof uri !== "string") {
    return null;
  }
  return { state, verifier, redirectUri: uri };
}

/** The session this tab holds, or null. An expired token is treated as no session. */
export function currentSession(): Session | null {
  const session = readSession();
  if (session === null) {
    return null;
  }
  return session.expiresAt > Date.now() ? session : null;
}

function redirectUri(): string {
  return `${window.location.origin}${CALLBACK_PATH}`;
}

/**
 * Send the browser to the provider's login page.
 *
 * `navigate` is a parameter so that the redirect is observable in a test. Production
 * passes nothing and the browser navigates.
 */
export async function beginSignIn(
  navigate: (url: string) => void = (url) => {
    window.location.assign(url);
  },
  options?: { loginHint?: string | null },
): Promise<void> {
  const config = await loadAuthConfig();
  const endpoints = await discoverEndpoints(config.issuer);

  const verifier = randomUrlSafe();
  const state = randomUrlSafe(16);
  const pending: PendingSignIn = { state, verifier, redirectUri: redirectUri() };
  sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));

  const parameters = new URLSearchParams({
    response_type: "code",
    client_id: config.clientId,
    redirect_uri: pending.redirectUri,
    scope: config.scope,
    state,
    nonce: randomUrlSafe(16),
    code_challenge: await codeChallengeS256(verifier),
    code_challenge_method: "S256",
  });
  // The persona bar's only lever: a preselected username on the provider's OWN page.
  // It never authenticates anything by itself (Ruling 17).
  if (options?.loginHint) {
    parameters.set("login_hint", options.loginHint);
  }
  navigate(`${endpoints.authorizationEndpoint}?${parameters.toString()}`);
}

/**
 * Finish the flow: check the state, exchange the code for a token, store the session.
 *
 * The state check is the cross-site request forgery defence for the callback. A response
 * whose state is not the one this tab sent is discarded without a token request.
 */
export async function completeSignIn(query: URLSearchParams): Promise<Session> {
  const pending = readPendingSignIn();
  sessionStorage.removeItem(PENDING_KEY);

  if (pending === null) {
    throw new Error("There is no sign-in in progress in this tab");
  }
  const returnedState = query.get("state");
  if (returnedState === null || returnedState !== pending.state) {
    throw new Error("The sign-in response did not match this tab's request");
  }
  const code = query.get("code");
  if (code === null) {
    throw new Error("The sign-in did not complete");
  }

  const config = await loadAuthConfig();
  const endpoints = await discoverEndpoints(config.issuer);
  const response = await fetch(endpoints.tokenEndpoint, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      client_id: config.clientId,
      code,
      redirect_uri: pending.redirectUri,
      code_verifier: pending.verifier,
    }).toString(),
  });
  if (!response.ok) {
    throw new Error(`The token request failed with ${String(response.status)}`);
  }

  const body: unknown = await response.json();
  const parsed = body as Record<string, unknown>;
  const accessToken = parsed.access_token;
  const expiresIn = parsed.expires_in;
  if (typeof accessToken !== "string" || typeof expiresIn !== "number") {
    throw new Error("The token response was not in the expected shape");
  }
  const session: Session = {
    accessToken,
    idToken: typeof parsed.id_token === "string" ? parsed.id_token : null,
    expiresAt: Date.now() + expiresIn * 1000,
  };
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  return session;
}

/**
 * End the session here and at the provider.
 *
 * Both halves are necessary: clearing local storage alone leaves the provider's session
 * cookie in place, so the next sign-in would complete without anyone being asked for
 * anything, which looks exactly like a sign-out that did not work.
 */
export async function signOut(
  navigate: (url: string) => void = (url) => {
    window.location.assign(url);
  },
): Promise<void> {
  // readSession, not currentSession: an expired access token still has an identity token
  // beside it, and that is what tells the provider whose session to end.
  const session = readSession();
  sessionStorage.removeItem(SESSION_KEY);
  sessionStorage.removeItem(PENDING_KEY);

  const config = await loadAuthConfig();
  const endpoints = await discoverEndpoints(config.issuer);
  const parameters = new URLSearchParams({
    // The trailing slash is load-bearing. The provider matches this against a registered
    // allow-list by exact string, and the entry the deploy registers is the application's
    // home page, "<origin>/". Sending "<origin>" produced a 400 and a sign-out that
    // silently did nothing. Observed in a browser, 2026-09-19.
    post_logout_redirect_uri: `${window.location.origin}/`,
    client_id: config.clientId,
  });
  if (session?.idToken != null) {
    parameters.set("id_token_hint", session.idToken);
  }
  navigate(`${endpoints.endSessionEndpoint}?${parameters.toString()}`);
}
